import { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { getItem } from '../utils/storage';
import { ensureSession, login, requiresSso } from '../utils/auth';
import { clearUser, getUserContext, apiSlice } from '@headless-shell/store';
import { HeaderLoginComplete, sendGA4Data } from '../analytics/events';
/**
 * Authentication hook for the App Shell.
 *
 * Delegates the actual session check to `authClient.ensureSession()`, which is
 * memoized so concurrent callers / StrictMode double-invocations share one
 * network round trip. This hook is responsible only for surfacing state and a
 * few shell-side side-effects (URL cleanup, GA4, getUserContext, auto-login).
 */
export const useAuthentication = (
  options = {},
  postLoginRedirectUriOverride = ''
) => {
  const [authenticated, setAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [authChecked, setAuthChecked] = useState(false);
  const [sessionCode, setSessionCode] = useState('');
  const [disableAuth] = useState(getItem('embed') === 'true');
  const dispatch = useDispatch();
  useEffect(() => {
    if (disableAuth) {
      setAuthenticated(true);
      setAuthChecked(true);
      setLoading(false);
      return undefined;
    }
    let cancelled = false;
    const finish = (isAuth) => {
      if (cancelled) return;
      setAuthenticated(isAuth);
      setAuthChecked(true);
      setLoading(false);
    };
    const handleFreshLoginUrlCleanup = () => {
      const urlParams = new URLSearchParams(window.location.search);
      const stateParam = urlParams.get('state');
      const expiresAtParam = urlParams.get('expires_at');
      if (!stateParam || !expiresAtParam) return;
      sendGA4Data(HeaderLoginComplete, { eventLabel: 'success' });
      urlParams.delete('state');
      urlParams.delete('expires_at');
      const qs = urlParams.toString();
      const newUrl = qs ? `${window.location.pathname}?${qs}` : window.location.pathname;
      window.history.replaceState({}, '', newUrl);
    };
    (async () => {
      try {
        const session = await ensureSession();
        if (!cancelled) {
          setSessionCode(session?.code || '');
        }
        if (cancelled) return;
        if (session.authenticated) {
          handleFreshLoginUrlCleanup();
          finish(true);
          return;
        }
        finish(false);
        const returnUrl = `${window.location.pathname}${window.location.search}${window.location.hash}`;
        // A LEGACY response can represent an expired legacy session. Keep
        // public routes available; protected MFEs request login through their
        // AuthenticationGate when they mount.
        const shouldRedirect = session.code !== 'LEGACY' && requiresSso(session.code);
        if (shouldRedirect) {
          await login(postLoginRedirectUriOverride, returnUrl);
        }
      } catch (e) {
         
        console.error('[useAuthentication] check failed', e);
        finish(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [disableAuth, postLoginRedirectUriOverride]);
  useEffect(() => {
    if (authenticated) {
      dispatch(getUserContext());
      // Invalidate ProductTags so the query re-fires with the user's profile
      dispatch(apiSlice.util.invalidateTags(['ProductTags']));
    }
    else{
      dispatch(clearUser());
    }
  }, [authenticated, dispatch]);
  return { authenticated, loading, authChecked, disableAuth, options, sessionCode };
};
